import { type InfiniteData } from '@reduxjs/toolkit/query';

import {
  type FrontendAssortmentRowDimensionIdentifier,
  type TransformedEditPreSeasonAggregationRequest,
  type TransformedGetPreSeasonAggregationsListRequest,
  type TransformedGetPreSeasonAggregationsListResponse,
} from '../types';

import { findPreSeasonQueryArgsToInvalidate } from './find-pre-season-query-args-to-invalidate';

export type PreSeasonInfiniteData = InfiniteData<
  TransformedGetPreSeasonAggregationsListResponse,
  unknown
>;

/**
 * Collect the identifiers of every row currently loaded in the cache entries
 * so we can silently update the WH quantities after IA
 */
export const findPreSeasonRowsToRefresh = (
  mutationQueryArgs: TransformedEditPreSeasonAggregationRequest,
  cachedQueryArgs: TransformedGetPreSeasonAggregationsListRequest[],
  getEntryData: (
    queryArgs: TransformedGetPreSeasonAggregationsListRequest,
  ) => PreSeasonInfiniteData | undefined,
): {
  keptQueryArgs: TransformedGetPreSeasonAggregationsListRequest[];
  rows: FrontendAssortmentRowDimensionIdentifier[][];
  rowIds: Set<string>;
} => {
  const invalidatedQueryArgs = new Set(
    findPreSeasonQueryArgsToInvalidate(cachedQueryArgs, mutationQueryArgs),
  );

  const keptQueryArgs = cachedQueryArgs.filter(
    (queryArgs) => !invalidatedQueryArgs.has(queryArgs),
  );

  const seenRowIds = new Set<string>();
  const rows: FrontendAssortmentRowDimensionIdentifier[][] = [];

  for (const queryArgs of keptQueryArgs) {
    const data = getEntryData(queryArgs);
    for (const item of data?.pages.flatMap((page) => page.items) ?? []) {
      if (seenRowIds.has(item.id)) continue;
      seenRowIds.add(item.id);
      // item.id is the JSON-serialised identifier row (see
      // transformPreSeasonAggregationItem), so it parses straight back into
      // the include_list wire format.
      rows.push(
        JSON.parse(item.id) as FrontendAssortmentRowDimensionIdentifier[],
      );
    }
  }

  return { keptQueryArgs, rows, rowIds: seenRowIds };
};
